import { Box, Group, Badge, Text, Stack, Button } from '@mantine/core';
import { Calendar, User, ExternalLink, Users } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import type { SessionDetailsForTask } from '../../../types';
import { formatDate, formatTime } from '../../../utils/formatters';
import { SESSION_STATUS_CONFIG } from '../../../config/statusConfig';
import { RsvpDisplay } from './RsvpDisplay';

interface RelatedSessionCardProps {
  session: SessionDetailsForTask;
  cohortId: string;
  onNavigate?: () => void;
}

/**
 * Get status badge configuration for session status
 */
function getSessionStatusConfig(status: string): { label: string; color: string } {
  const config = SESSION_STATUS_CONFIG[status];
  return config
    ? { label: config.label, color: config.color }
    : { label: status.toUpperCase(), color: 'gray' };
}

/**
 * Icon wrapper component with styled background
 */
function IconWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Box
      style={{
        width: 32,
        height: 32,
        borderRadius: 6,
        background: 'rgba(255, 255, 255, 0.70)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
}

/**
 * Data row component for session details
 */
function DataRow({
  icon,
  label,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Group gap="sm" wrap="nowrap">
      <IconWrapper>{icon}</IconWrapper>
      <Stack gap={2}>
        <Text
          size="10px"
          c="#888888"
          tt="uppercase"
          fw={500}
          style={{ letterSpacing: '0.3px' }}
        >
          {label}
        </Text>
        {children}
      </Stack>
    </Group>
  );
}

/**
 * Card component displaying related session information for a task
 * MED-163: Redesigned per QA feedback
 * MED-258: Fixed session link, RSVP display, and date/time format
 */
export function RelatedSessionCard({ session, cohortId, onNavigate }: RelatedSessionCardProps) {
  const navigate = useNavigate();
  const statusConfig = getSessionStatusConfig(session.status);

  // Format date and time separately to match Sessions tab display
  const dateDisplay = session.scheduled_start
    ? formatDate(session.scheduled_start)
    : 'TBD';
  const timeDisplay = session.scheduled_start
    ? formatTime(session.scheduled_start)
    : '';
  const dateTimeDisplay = timeDisplay
    ? `${dateDisplay} at ${timeDisplay}`
    : dateDisplay;

  // Session title with number
  const sessionTitle = session.session_number
    ? `Session ${session.session_number}: ${session.title}`
    : session.title;

  return (
    <Box
      p="md"
      style={{
        backgroundColor: '#F9F7F4',
        borderRadius: 10,
      }}
    >
      <Stack gap="md">
        {/* Header: Session title + status badge */}
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Text size="15px" c="#28463A" fw={500} style={{ flex: 1 }}>
            {sessionTitle}
          </Text>
          <Badge size="sm" color={statusConfig.color} variant="filled">
            {statusConfig.label}
          </Badge>
        </Group>

        {/* Date & Time */}
        <DataRow
          icon={<Calendar size={16} color="#28463A" />}
          label="Date & Time"
        >
          <Text size="13px" c="#28463A">
            {dateTimeDisplay}
          </Text>
        </DataRow>

        {/* RSVP - show actual data via RsvpDisplay */}
        <DataRow
          icon={<Users size={16} color="#28463A" />}
          label="Expected"
        >
          <RsvpDisplay
            summary={session.rsvp_summary}
            isFinalized={!!session.finalized_at}
          />
        </DataRow>

        {/* Coach */}
        {session.coach_name && (
          <DataRow
            icon={<User size={16} color="#28463A" />}
            label="Coach"
          >
            <Text size="13px" c="#28463A">
              {session.coach_name}
            </Text>
          </DataRow>
        )}

        {/* Go to Session button - navigates to cohort Sessions tab */}
        <Button
          size="sm"
          variant="filled"
          color="medleyGreen"
          leftSection={<ExternalLink size={14} />}
          onClick={() => {
            onNavigate?.();
            navigate(`/cohorts/${cohortId}?tab=sessions`);
          }}
          fullWidth
        >
          Go to Session
        </Button>
      </Stack>
    </Box>
  );
}
